<template>
  <b-container
    class="mb-5"
    fluid
  >
    <b-breadcrumb
      v-if="$route.meta.breadcrumb"
      :items="$route.meta.breadcrumb || []"
    />

    <b-card>
      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <b-form-group>
            <label for="date_from">Date From</label>
            <b-input
              id="date_from"
              v-model="filter.date_from"
              type="date"
              @change="onFilterChanged"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          sm="6"
        >
          <b-form-group>
            <label for="date_to">Date To</label>
            <b-input
              id="date_to"
              v-model="filter.date_to"
              type="date"
              @change="onFilterChanged"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          sm="6"
        >
          <div class="form-group">
            <label>Filter By Status</label>
            <b-select
              v-model="filter.filter_status"
              :options="filterStatuses"
              @change="onFilterChanged"
            />
          </div>
        </b-col>

        <b-col
          cols="12"
          sm="6"
        >
          <b-form-group>
            <label>Search String</label>
            <b-input
              v-model="tableSettings.filter"
              type="text"
              placeholder="Enter search string"
              debounce="1000"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-table
        ref="table"
        hover
        responsive
        class="mt-2"
        :per-page="tableSettings.perPage"
        :current-page="tableSettings.currentPage"
        :items="myProvider"
        :fields="tableSettings.fields"
        :sort-by.sync="tableSettings.sortBy"
        :sort-desc.sync="tableSettings.sortDesc"
        :sort-direction="tableSettings.sortDirection"
        :filter="tableSettings.filter"
        :filter-included-fields="tableSettings.filterOn"
        show-empty
      >
        <template #cell(index)="data">
          {{ tableSettings.currentPage * tableSettings.perPage - tableSettings.perPage + (data.index + 1) }}
        </template>

        <template #cell()="data">
          <div class="text-nowrap">
            {{ data.value }}
          </div>
        </template>

        <template #cell(actions)="row">
          <div class="text-nowrap">
            <b-button
              :disabled="row.item.status !== 'Processing'"
              size="sm"
              @click="onShowEditForm(row.item)"
            >
              Update
            </b-button>
          </div>
        </template>

      </b-table>

      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <div class="w-100 w-sm-25 mb-2 sm-mb-2">
            <b-select
              v-model="tableSettings.perPage"
              :options="tableSettings.pageOptions"
              size="sm"
            />
          </div>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          class="d-flex justify-content-center justify-content-sm-end"
        >
          <b-pagination
            v-model="tableSettings.currentPage"
            :total-rows="tableSettings.totalRows"
            :per-page="tableSettings.perPage"
            first-number
            last-number
            pills
            prev-text="Prev"
            next-text="Next"
            aria-controls="table"
          />
        </b-col>
      </b-row>
    </b-card>

    <b-modal
      id="modal-edit-form"
      scrollable
      no-close-on-backdrop
      title="Update Log"
      @ok="onConfirmAction"
    >

      <ValidationObserver ref="form">
        <form
          role="form"
          novalidate
        >
          <b-form-group>
            <label for="customer_name"><strong>Customer Name</strong></label>
            <b-input
              id="customer_name"
              v-model="smsLog.customer_name"
              disabled
            />
          </b-form-group>

          <b-form-group>
            <label for="mobile_number"><strong>Mobile Number</strong></label>
            <b-input
              id="mobile_number"
              v-model="smsLog.mobile_number"
              disabled
            />
          </b-form-group>

          <b-form-group>
            <label for="promotion_name"><strong>Promotion Name</strong></label>
            <b-input
              id="promotion_name"
              v-model="smsLog.promotion_name"
              disabled
            />
          </b-form-group>

          <ValidationProvider
            #default="{ errors }"
            name="status"
            vid="status"
            rules="required"
          >
            <b-form-group>
              <label><strong>Status</strong></label>
              <b-select
                v-model="smsLog.status"
                :options="options.statuses"
                :state="errors.length > 0 ? false : null"
                :disabled="state.busy"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
              >
                <span>{{ errors[0] }}</span>
              </div>
            </b-form-group>
          </ValidationProvider>

          <ValidationProvider
            #default="{ errors }"
            name="sent at"
            vid="sent_at"
            :rules="rulesSentAt"
          >
            <b-form-group>
              <label for="sent_at"><strong>Sent At</strong></label>
              <b-input
                id="sent_at"
                v-model="smsLog.sent_at"
                type="text"
                :state="errors.length > 0 ? false : null"
                :disabled="state.busy || smsLog.status === 'Processing'"
                autocomplete="off"
                maxlength="20"
              />
              <div
                v-if="errors.length > 0"
                class="invalid-feedback"
              >
                <span>{{ errors[0] }}</span>
              </div>
            </b-form-group>
          </ValidationProvider>

        </form>
      </ValidationObserver>

      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="success"
          :disabled="state.busy"
          @click="ok()"
        >
          {{ state.editing ? "Update Record" : "Save Record" }}
        </b-button>
        <b-button
          variant="danger"
          :disabled="state.busy"
          @click="cancel()"
        >
          Cancel
        </b-button>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import _ from 'lodash'
import { core } from '@/config/pluginInit'
import { AgentUtilitySMSService, AxiosError } from '@/services'
import formatter from '@/mixins/formatter'

export default {
  name: 'AgentUtilityService',

  middleware: ['auth', 'agent'],

  metaInfo () {
    return {
      title: 'SMS'
    }
  },

  mixins: [formatter],

  data () {
    return {
      state: {
        busy: false
      },
      filter: {
        date_from: this.dateFormatter(Date.now()),
        date_to: this.dateFormatter(Date.now()),
        filter_status: 'All'
      },
      smsLog: {
        id: 0,
        customer_name: '',
        mobile_number: '',
        promotion_name: '',
        status: null,
        sent_at: ''
      },
      options: {
        statuses: [
          { text: 'Sent', value: 'Sent' },
          { text: 'Failed', value: 'Failed' },
          { text: 'Processing', value: 'Processing' }
        ]
      },
      tableSettings: {
        perPage: 10,
        pageOptions: [10, 25, 50, 100],
        totalRows: 0,
        currentPage: 1,
        sortBy: '',
        sortDesc: false,
        sortDirection: 'asc',
        filter: '',
        filterOn: [],
        fields: [
          'index',
          'actions',
          { key: 'called_at', formatter: this.Formatter, sortable: true },
          { key: 'customer_name' },
          { key: 'mobile_number' },
          { key: 'promotion_name' },
          { key: 'status' },
          { key: 'reason' },
          { key: 'sent_at', formatter: this.dateTimeFormatter, sortable: true },
          { key: 'created_at', formatter: this.dateTimeFormatter, sortable: true },
          { key: 'updated_at', formatter: this.dateTimeFormatter, sortable: true }
        ]
      }
    }
  },

  computed: {
    filterStatuses () {
      return [{ text: 'All', value: 'All' }].concat(this.options.statuses)
    },

    rulesSentAt () {
      if (this.smsLog.status === 'Sent' || this.smsLog.status === 'Failed') {
        return 'required|min:20|max:20'
      }

      return ''
    }
  },

  watch: {
    'smsLog.status' (value) {
      if (value === 'Processing') {
        this.smsLog.sent_at = ''
      }
    }
  },

  mounted () {
    core.index()
  },

  methods: {
    async myProvider (ctx) {
      try {
        const { data } = await AgentUtilitySMSService.get(
          this.objectToUrl({
            page: ctx.currentPage,
            per_page: ctx.perPage,
            sort: ctx.sortBy,
            sort_desc: ctx.sortDesc,
            filter_from: this.filter.date_from,
            filter_to: this.filter.date_to,
            filter_status: this.filter.filter_status,
            filter_text: ctx.filter
          })
        )
        this.tableSettings.totalRows = data.total_rows
        return data.items
      } catch {
        return []
      }
    },

    onShowEditForm (current) {
      if (current.status !== 'Processing') {
        return
      }

      this.state.editing = true
      this.smsLog.id = current.id
      this.smsLog.customer_name = current.customer_name
      this.smsLog.mobile_number = current.mobile_number
      this.smsLog.promotion_name = current.promotion_name
      this.smsLog.status = current.status
      this.smsLog.sent_at = ''

      this.$bvModal.show('modal-edit-form')
    },

    async onConfirmAction (bvModalEvt) {
      bvModalEvt.preventDefault()
      await this.$refs.form.validate().then(async success => {
        if (success) {
          this.$swal.fire({
            icon: 'question',
            title: 'Question',
            text: 'Do you really want to save changes?',
            confirmButtonColor: '#06C270',
            confirmButtonText: 'Yes',
            cancelButtonColor: '#FF2929',
            showCancelButton: true,
            showLoaderOnConfirm: true,
            preConfirm: () => {
              return this.onFormPutSubmit()
            },
            allowOutsideClick: () => !this.$swal.isLoading()
          })
        } else {
          this.$bvModal.msgBoxOk('Oops! There were problem with your inputs.', {
            title: 'Validation Error',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            centered: true
          })
        }
      })
    },

    async onFormPutSubmit () {
      return new Promise(resolve => {
        this.state.busy = true
        AgentUtilitySMSService.put(this.smsLog).then(({ data }) => {
          this.$bvModal.hide('modal-edit-form')
          this.state.busy = false
          this.$swal.fire({
            icon: 'success',
            title: 'Successful',
            text: data.message,
            confirmButtonColor: '#06C270',
            confirmButtonText: 'Dismiss'
          }).then(() => {
            const row = _.find(this.$refs.table.localItems, { id: data.sms_log.id })
            row.active = data.sms_log.active
            row.customer_name = data.sms_log.customer_name
            row.mobile_number = data.sms_log.mobile_number
            row.promotion_name = data.sms_log.promotion_name
            row.status = data.sms_log.status
            row.sent_at = data.sms_log.sent_at
          })
        }).catch(error => {
          this.state.busy = false
          if (error instanceof AxiosError) {
            if (error.code === 422) {
              this.$bvModal.msgBoxOk('Oops! There were problem with your inputs.', {
                title: 'Validation Error',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                centered: true
              })
              this.$refs.form.setErrors(error.message)
              resolve(error)
            }
          }
        })
      })
    },

    onFilterChanged () {
      this.$refs.table.refresh()
    }
  }
}
</script>
